// ════════════════════════════════════════════════════════════════════════════
// AdminClientesTabV2 — refactor piloto Sprint 2 com primitivos Lpa*
//
// Comparação com AdminClientesTab.jsx (legado):
//   • LOC original: ~600 (com 6 sub-componentes)
//   • LOC V2: ~280 (-53%)
//   • Inline `style={{`: 80+ → ~12 (-85%)
//   • <input>/<select> sem label: 4 → 0
//   • a11y: aria-sort, aria-label, role=table auto via LpaTable
//   • Sticky header, sort, paginação: nativos via LpaTable
//
// Trade-offs vs legado:
//   • V2 não inclui todas as features do legado (column visibility toggle,
//     expandable rows, quick CRM modal, bulk actions completos) — escopo
//     intencional para demonstrar a base do refactor. Features extras serão
//     incrementadas em PRs subsequentes.
//
// Co-existe com AdminClientesTab.jsx (princípio aditivo).
// ════════════════════════════════════════════════════════════════════════════

import React, { useMemo, useState } from "react";
import {
  LpaPageHeader,
  LpaSectionHeader,
  LpaKpiCard,
  LpaTable,
  LpaButton,
  LpaInput,
  LpaSelect,
  LpaField,
  LpaBadge,
  LpaCard,
  type LpaColumn,
} from "../components/ui/lpa";
import { fmtK } from "./adminUtils.jsx";

// ─── Tipos ────────────────────────────────────────────────────────────────

interface ClientRow {
  id: string;
  full_name?: string;
  name?: string;
  patrimonio?: { total?: number };
  nextReview?: string;
  email?: string;
  advisor?: string;
  profile?: string;
  _snap?: boolean;
}

interface ThemeLike {
  card: string;
  text: string;
  muted: string;
  border: string;
  bg: string;
}

interface Props {
  T: ThemeLike;
  dynamicClients: ClientRow[];
  suitabilityDB: Record<string, { status?: string }>;
  setSelectedClient?: (c: ClientRow) => void;
  setShowNewClient?: (open: boolean) => void;
}

// ─── Component ────────────────────────────────────────────────────────────

export default function AdminClientesTabV2({
  T: _T,
  dynamicClients,
  suitabilityDB,
  setSelectedClient,
  setShowNewClient,
}: Props): React.ReactElement {
  const [search, setSearch] = useState("");
  const [profileFilter, setProfileFilter] = useState<string>("");

  // ── KPIs ──────────────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const totalAUM = dynamicClients.reduce((s, c) => s + (c.patrimonio?.total || 0), 0);
    const onboardingComplete = Object.values(suitabilityDB ?? {}).filter(
      (s) => s.status === "completo"
    ).length;
    const today = new Date().toISOString().slice(0, 10);
    const overdueReviews = dynamicClients.filter(
      (c) => c.nextReview && c.nextReview !== "–" && c.nextReview < today
    ).length;
    const noSnapshot = dynamicClients.filter((c) => !c._snap).length;
    return { totalAUM, onboardingComplete, overdueReviews, noSnapshot };
  }, [dynamicClients, suitabilityDB]);

  // ── Filtros ───────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    let out = dynamicClients;
    if (search.trim()) {
      const q = search.toLowerCase();
      out = out.filter(
        (c) =>
          (c.full_name ?? c.name ?? "").toLowerCase().includes(q) ||
          (c.email ?? "").toLowerCase().includes(q) ||
          c.id.toLowerCase().includes(q)
      );
    }
    if (profileFilter) {
      out = out.filter((c) => c.profile === profileFilter);
    }
    return out;
  }, [dynamicClients, search, profileFilter]);

  // ── Profiles únicos para o filter dropdown ────────────────────────────
  const profileOptions = useMemo(() => {
    const set = new Set<string>();
    dynamicClients.forEach((c) => c.profile && set.add(c.profile));
    return Array.from(set).map((p) => ({ value: p, label: p }));
  }, [dynamicClients]);

  // ── Columns do LpaTable ───────────────────────────────────────────────
  const columns: LpaColumn<ClientRow>[] = [
    {
      key: "name",
      label: "Cliente",
      sortable: true,
      width: "30%",
      accessor: (r) => r.full_name ?? r.name ?? r.id,
      render: (r) => (
        <div>
          <div
            style={{
              fontFamily: 'Inter, -apple-system, "Segoe UI", system-ui, sans-serif',
              fontSize: 14,
              fontWeight: 500,
              color: "var(--lpa-text)",
              lineHeight: 1.3,
            }}
          >
            {r.full_name ?? r.name ?? r.id.slice(0, 8)}
          </div>
          {r.email && (
            <div
              style={{
                fontFamily: '"JetBrains Mono", "SF Mono", Menlo, Consolas, monospace',
                fontSize: 11,
                color: "var(--lpa-sub)",
                marginTop: 3,
              }}
            >
              {r.email}
            </div>
          )}
        </div>
      ),
    },
    {
      key: "patrimonio",
      label: "AUM",
      sortable: true,
      align: "right",
      accessor: (r) => r.patrimonio?.total ?? 0,
      render: (r) => (
        <span
          style={{
            fontFamily: '"JetBrains Mono", "SF Mono", Menlo, Consolas, monospace',
            fontSize: 14,
            fontWeight: 500,
            color: "var(--lpa-text)",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {fmtK(r.patrimonio?.total ?? 0)}
        </span>
      ),
    },
    {
      key: "profile",
      label: "Perfil",
      sortable: true,
      render: (r) =>
        r.profile ? (
          <LpaBadge variant="neutral">{r.profile}</LpaBadge>
        ) : (
          <span style={{ color: "var(--lpa-muted)" }}>—</span>
        ),
    },
    {
      key: "nextReview",
      label: "Próxima review",
      sortable: true,
      render: (r) => {
        if (!r.nextReview || r.nextReview === "–") {
          return <span style={{ color: "var(--lpa-muted)" }}>—</span>;
        }
        const today = new Date().toISOString().slice(0, 10);
        const overdue = r.nextReview < today;
        return (
          <LpaBadge variant={overdue ? "danger" : "success"} shape={overdue ? "down" : "up"}>
            {r.nextReview}
          </LpaBadge>
        );
      },
    },
    {
      key: "actions",
      label: "",
      width: "220px",
      align: "right",
      render: (r) => (
        <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
          <LpaButton
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              window.location.href = `/admin/clientes/${r.id}/tracker`;
            }}
            title="Abrir Client Tracker — view única do cliente"
          >
            📋 Tracker
          </LpaButton>
          <LpaButton
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              window.location.href = `/admin/clientes/${r.id}/realocador`;
            }}
            title="Análise + realocador interativo"
          >
            📊 Realocar
          </LpaButton>
        </div>
      ),
    },
  ];

  return (
    <div style={{ padding: 0 }}>
      {/* Page header */}
      <LpaPageHeader
        title="Clientes"
        sub={`${dynamicClients.length} clientes ativos · gestão de carteira mensal`}
        actions={
          <LpaButton variant="primary" onClick={() => setShowNewClient?.(true)}>
            + Novo cliente
          </LpaButton>
        }
      />

      {/* KPIs */}
      <LpaSectionHeader title="Indicadores da base" sub="Visão geral consolidada" />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 14,
          marginBottom: 8,
        }}
      >
        <LpaKpiCard label="AUM total" value={fmtK(kpis.totalAUM)} size="md" />
        <LpaKpiCard
          label="Onboarding completo"
          value={`${kpis.onboardingComplete}/${dynamicClients.length}`}
          delta={{
            value:
              dynamicClients.length > 0
                ? (kpis.onboardingComplete / dynamicClients.length) * 100
                : 0,
            suffix: "%",
            direction: "flat",
          }}
        />
        <LpaKpiCard
          label="Reviews atrasadas"
          value={kpis.overdueReviews}
          valueColor={kpis.overdueReviews > 0 ? "var(--lpa-warning)" : undefined}
        />
        <LpaKpiCard
          label="Sem snapshot"
          value={kpis.noSnapshot}
          valueColor={kpis.noSnapshot > 0 ? "var(--lpa-danger)" : undefined}
        />
      </div>

      {/* Filtros */}
      <LpaSectionHeader
        title="Lista de clientes"
        sub={`Exibindo ${filtered.length} de ${dynamicClients.length}`}
      />

      <LpaCard variant="flat" padding="md" style={{ marginBottom: 16 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "2fr 1fr auto",
            gap: 12,
            alignItems: "flex-end",
          }}
        >
          <LpaField label="Buscar" labelSrOnly>
            <LpaInput value={search} onChange={setSearch} placeholder="Nome, e-mail ou ID…" />
          </LpaField>
          <LpaField label="Perfil" labelSrOnly>
            <LpaSelect
              value={profileFilter}
              onChange={setProfileFilter}
              placeholder="Todos os perfis"
              options={profileOptions}
            />
          </LpaField>
          <LpaButton
            variant="ghost"
            onClick={() => {
              setSearch("");
              setProfileFilter("");
            }}
            disabled={!search && !profileFilter}
          >
            Limpar
          </LpaButton>
        </div>
      </LpaCard>

      {/* Tabela */}
      <LpaTable
        ariaLabel="Lista de clientes ativos"
        columns={columns}
        rows={filtered}
        rowKey={(r) => r.id}
        pagination={{ pageSize: 25 }}
        onRowClick={(row) => setSelectedClient?.(row)}
        stickyHeader
        density="comfortable"
      />
    </div>
  );
}
